import {
  Component,
  ChangeDetectionStrategy,
  signal,
  computed,
  effect,
  Signal,
  afterNextRender,
  inject
} from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';

import { SearchSortDirection, SymetraDateTime } from '@benefitsui/app/api/benefits-clients';
import { DateTimeUtilities } from '@benefitsui/app/shared/utilities/dateTimeUtilities';
import { PageHeaderService } from '@benefitsui/app/services/page-header/page-header-service';

import { IClaimData, ITableHeader, getClaimTypeDisplayUi } from '../list/data/claim-data.types';
import {
  ClaimStatusEnum,
  StatusColorEnum,
  KeyDateLabelEnum,
  ProgressSectionLabelEnum,
  EmployeeLabelEnum,
  BenefitLabelEnum,
  ClaimLabelEnum,
  ProgressTrackerLabelEnum,
  PaymentFieldEnum,
  PaymentLabelEnum,
  CommonDisplayTextEnum,
  WaiverStatusTextEnum,
  ClientClaimTypeEnum
} from '../list/data/claim-data-enums';
import { LocalClaimsDataReaderService } from '../list/data/local-claims-data-reader.service';
import {
  IProgressStep,
  IPaymentTableItem,
  IClaimBenefit,
  IBenefitStatus,
  IProgressTrackerItem
} from '../list/data/claim-details.model';
import { StatusIndicatorInput } from '@benefitsui/app/reusable-components/list-card/list-card.components.types';
import { StateUtilities } from '@benefitsui/app/shared/utilities/state-utilities';

@Component({
  selector: 'app-claim-detail',
  templateUrl: './detail.component.html',
  styleUrls: ['./detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ClaimDetailComponent {
  private static readonly PAGE_TITLE = 'Claim Details';

  private route = inject<ActivatedRoute>(ActivatedRoute);
  private router = inject(Router);
  private title = inject(Title);
  private pageHeaderService = inject(PageHeaderService);
  private localClaimsDataReaderService = inject(LocalClaimsDataReaderService);

  claimData: IClaimData | null = null; // Keep for backward compatibility
  claimId: string | null = null;
  private isLoadingSignal = signal(true);
  
  readonly claimDataSignal = signal<IClaimData | null>(null);

  private readonly routeParams: Signal<Params>;

  readonly claimIdSignal: Signal<string | null>;

  readonly headerText: Signal<string>;

  readonly isLoading = computed(() => this.isLoadingSignal());

  readonly KeyDateLabel = KeyDateLabelEnum;
  readonly ProgressSectionLabel = ProgressSectionLabelEnum;
  readonly EmployeeLabel = EmployeeLabelEnum;
  readonly BenefitLabel = BenefitLabelEnum;
  readonly ClaimLabel = ClaimLabelEnum;
  readonly ProgressTrackerLabel = ProgressTrackerLabelEnum;

  readonly paymentTableItems = computed(() => {
    const claim = this.claimDataSignal();
    
    if (!claim?.payments?.length) {
      return [];
    }
    
    const mappedItems = claim.payments.map((payment) => ({
      checkNumber: payment.checkNumber,
      coverageType: payment.coverage,
      payee: payment.payee,
      printDate: this.formatDate(payment.printDate),
      amountPaid: payment.amountPaid,
      interestPaid: payment.interestPaid,
      netPaid: payment.netPayment
    }));
    
    return this.applySorting(mappedItems);
  });

  paymentTableHeaders: ITableHeader[] = [
    { text: PaymentLabelEnum.CheckNumber, key: PaymentFieldEnum.CheckNumber, sortable: true },
    { text: PaymentLabelEnum.CoverageType, key: PaymentFieldEnum.CoverageType, sortable: true },
    { text: PaymentLabelEnum.Payee, key: PaymentFieldEnum.Payee, sortable: true },
    { text: PaymentLabelEnum.PrintDate, key: PaymentFieldEnum.PrintDate, sortable: true },
    { text: PaymentLabelEnum.AmountPaid, key: PaymentFieldEnum.AmountPaid, sortable: true },
    { text: PaymentLabelEnum.InterestPaid, key: PaymentFieldEnum.InterestPaid, sortable: true },
    { text: PaymentLabelEnum.NetPaid, key: PaymentFieldEnum.NetPaid, sortable: true }
  ];

  private paymentSortFieldSignal = signal<PaymentFieldEnum>(PaymentFieldEnum.PrintDate);
  private paymentSortDirectionSignal = signal<SearchSortDirection>(SearchSortDirection.Descending);

  // Getter/setter properties for backward compatibility
  get paymentSortField(): PaymentFieldEnum {
    return this.paymentSortFieldSignal();
  }

  set paymentSortField(value: PaymentFieldEnum) {
    this.paymentSortFieldSignal.set(value);
  }

  get paymentSortDirection(): SearchSortDirection {
    return this.paymentSortDirectionSignal();
  }

  set paymentSortDirection(value: SearchSortDirection) {
    this.paymentSortDirectionSignal.set(value);
  }

  // Computed signals for payment table headers (performance optimization with lazy loading)
  readonly paymentHeaderTexts = computed(() => {
    if (!this.claimDataSignal()?.payments?.length) return {};
    
    return {
      [PaymentFieldEnum.CheckNumber]: PaymentLabelEnum.CheckNumber,
      [PaymentFieldEnum.CoverageType]: PaymentLabelEnum.CoverageType,
      [PaymentFieldEnum.Payee]: PaymentLabelEnum.Payee,
      [PaymentFieldEnum.PrintDate]: PaymentLabelEnum.PrintDate,
      [PaymentFieldEnum.AmountPaid]: PaymentLabelEnum.AmountPaid,
      [PaymentFieldEnum.InterestPaid]: PaymentLabelEnum.InterestPaid,
      [PaymentFieldEnum.NetPaid]: PaymentLabelEnum.NetPaid
    };
  });

  readonly paymentHeaderAriaLabels = computed(() => {
    if (!this.claimDataSignal()?.payments?.length) return {};
    
    const sortField = this.paymentSortFieldSignal();
    const sortDirection = this.paymentSortDirectionSignal();
    const headerTexts = this.paymentHeaderTexts();

    const getSortStatus = (field: PaymentFieldEnum): string => {
      if (sortField === field) {
        return sortDirection.toString();
      }
      return 'not sorted';
    };

    return {
      [PaymentFieldEnum.CheckNumber]: `Sort by ${headerTexts[PaymentFieldEnum.CheckNumber]}, currently sorted ${getSortStatus(PaymentFieldEnum.CheckNumber)}`,
      [PaymentFieldEnum.CoverageType]: `Sort by ${headerTexts[PaymentFieldEnum.CoverageType]}, currently sorted ${getSortStatus(PaymentFieldEnum.CoverageType)}`,
      [PaymentFieldEnum.Payee]: `Sort by ${headerTexts[PaymentFieldEnum.Payee]}, currently sorted ${getSortStatus(PaymentFieldEnum.Payee)}`,
      [PaymentFieldEnum.PrintDate]: `Sort by ${headerTexts[PaymentFieldEnum.PrintDate]}, currently sorted ${getSortStatus(PaymentFieldEnum.PrintDate)}`,
      [PaymentFieldEnum.AmountPaid]: `Sort by ${headerTexts[PaymentFieldEnum.AmountPaid]}, currently sorted ${getSortStatus(PaymentFieldEnum.AmountPaid)}`,
      [PaymentFieldEnum.InterestPaid]: `Sort by ${headerTexts[PaymentFieldEnum.InterestPaid]}, currently sorted ${getSortStatus(PaymentFieldEnum.InterestPaid)}`,
      [PaymentFieldEnum.NetPaid]: `Sort by ${headerTexts[PaymentFieldEnum.NetPaid]}, currently sorted ${getSortStatus(PaymentFieldEnum.NetPaid)}`
    };
  });

  readonly paymentHeaderAriaSortValues = computed(() => {
    if (!this.claimDataSignal()?.payments?.length) return {};
    
    const sortField = this.paymentSortFieldSignal();
    const sortDirection = this.paymentSortDirectionSignal();

    const getSortValue = (field: PaymentFieldEnum): string => {
      if (sortField === field) {
        return sortDirection.toString();
      }
      return 'none';
    };

    return {
      [PaymentFieldEnum.CheckNumber]: getSortValue(PaymentFieldEnum.CheckNumber),
      [PaymentFieldEnum.CoverageType]: getSortValue(PaymentFieldEnum.CoverageType),
      [PaymentFieldEnum.Payee]: getSortValue(PaymentFieldEnum.Payee),
      [PaymentFieldEnum.PrintDate]: getSortValue(PaymentFieldEnum.PrintDate),
      [PaymentFieldEnum.AmountPaid]: getSortValue(PaymentFieldEnum.AmountPaid),
      [PaymentFieldEnum.InterestPaid]: getSortValue(PaymentFieldEnum.InterestPaid),
      [PaymentFieldEnum.NetPaid]: getSortValue(PaymentFieldEnum.NetPaid)
    };
  });

  readonly paymentSortIconAscending = computed(() => {
    if (!this.claimDataSignal()?.payments?.length) return {};
    
    const sortField = this.paymentSortFieldSignal();
    const sortDirection = this.paymentSortDirectionSignal();

    const isAscending = (field: PaymentFieldEnum): boolean => {
      return sortDirection === SearchSortDirection.Ascending && sortField === field;
    };

    return {
      [PaymentFieldEnum.CheckNumber]: isAscending(PaymentFieldEnum.CheckNumber),
      [PaymentFieldEnum.CoverageType]: isAscending(PaymentFieldEnum.CoverageType),
      [PaymentFieldEnum.Payee]: isAscending(PaymentFieldEnum.Payee),
      [PaymentFieldEnum.PrintDate]: isAscending(PaymentFieldEnum.PrintDate),
      [PaymentFieldEnum.AmountPaid]: isAscending(PaymentFieldEnum.AmountPaid),
      [PaymentFieldEnum.InterestPaid]: isAscending(PaymentFieldEnum.InterestPaid),
      [PaymentFieldEnum.NetPaid]: isAscending(PaymentFieldEnum.NetPaid)
    } as Record<string, boolean>;
  });

  PaymentField = PaymentFieldEnum;
  ClaimSortDirection = SearchSortDirection;

  constructor() {
    this.routeParams = toSignal(this.route.params, { initialValue: {} });

    this.claimIdSignal = computed(() => {
      const params = this.routeParams();
      return params?.['id'] || null;
    });

    this.headerText = computed(() => {
      const claim = this.claimDataSignal();
      const claimId = this.claimIdSignal();

      // Priority: Use claimId from loaded data first, then fallback to route param
      if (claim?.claimDetails?.claimId) {
        return ClaimDetailComponent.PAGE_TITLE;
      }

      // Always ensure we have a claimId in the header, even during loading
      if (claimId) {
        return ClaimDetailComponent.PAGE_TITLE;
      }

      // Final fallback - this should rarely be reached
      return ClaimDetailComponent.PAGE_TITLE;
    });

    // Ensure header is set after next render
    // This handles page refresh scenarios where route params might not be immediately available
    afterNextRender(() => {
      const claim = this.claimDataSignal();
      const claimId = this.claimIdSignal();
      
      // Get the claim ID for subtitle
      let subtitle: string | undefined;
      if (claim?.claimDetails?.claimId) {
        subtitle = claim.claimDetails.claimId;
      } else if (claimId) {
        subtitle = claimId;
      }

      this.pageHeaderService.setHeaderData({
        title: ClaimDetailComponent.PAGE_TITLE,
        subtitle: subtitle
      });
    });

    effect(() => {
      const claimId = this.claimIdSignal();
      if (claimId) {
        this.claimId = claimId;

        const initialTitle = `${ClaimDetailComponent.PAGE_TITLE} (${claimId})`;
        this.title.setTitle(initialTitle);

        // Update page header with subtitle
        this.pageHeaderService.setHeaderData({
          title: ClaimDetailComponent.PAGE_TITLE,
          subtitle: claimId
        });

        this.loadClaimDetail(claimId);
      }
    });
  }

  formatDate(date: SymetraDateTime | string | undefined | null): string {
    if (!date) {
      return '00/00/0000';
    }

    if (typeof date === 'string') {
      // Parse date string as local date to avoid timezone issues
      const dateMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        const parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return parsedDate.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        });
      }

      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        return '00/00/0000';
      }
      return parsedDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    }

    if (date.month === undefined || date.day === undefined || date.year === undefined) {
      return '00/00/0000';
    }
    return DateTimeUtilities.symetraDateTimeToString(date, DateTimeUtilities.MonthDayYearFormat);
  }

  /**
   * Formats a birthdate for display in MM/DD/**** format
   */
  formatBirthdateForDisplay(date: SymetraDateTime | string | null | undefined): string {
    if (!date) {
      return '';
    }

    if (typeof date === 'string') {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        return '';
      }

      const month = (parsedDate.getMonth() + 1).toString().padStart(2, '0');
      const day = parsedDate.getDate().toString().padStart(2, '0');
      return `${month}/${day}/****`;
    }

    if (!date.month || !date.day) {
      return '';
    }

    // Mask the year for privacy (replace with asterisks)
    const maskedDate = { ...date, year: 0 };

    return DateTimeUtilities.symetraDateTimeToString(maskedDate, DateTimeUtilities.MonthDayYearFormat).replace(
      /\/\d{4}$/,
      '/****'
    );
  }

  /**
   * Calculates countdown from today to the effective date for waiver of premium
   */
  getCountdownToWaiver(effectiveDate: string | SymetraDateTime | undefined): string {
    if (!effectiveDate) {
      return '';
    }

    let effective: Date;

    // Handle string dates (format: YYYY-MM-DD)
    if (typeof effectiveDate === 'string') {
      const dateMatch = effectiveDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!dateMatch) {
        return '';
      }

      const [, year, month, day] = dateMatch;
      effective = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      // Handle SymetraDateTime objects
      if (!effectiveDate.year || !effectiveDate.month || !effectiveDate.day) {
        return '';
      }
      effective = new Date(effectiveDate.year, effectiveDate.month - 1, effectiveDate.day);
    }

    const today = new Date();

    // Set both dates to start of day for accurate day calculation
    today.setHours(0, 0, 0, 0);
    effective.setHours(0, 0, 0, 0);

    const diffTime = effective.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return WaiverStatusTextEnum.EffectiveNow;
    }
    if (diffDays === 0) {
      return WaiverStatusTextEnum.EffectiveToday;
    }
    if (diffDays === 1) {
      return WaiverStatusTextEnum.OneDay;
    }

    return `${diffDays} days`;
  }

  isEffectiveDateInFuture(effectiveDate: string | SymetraDateTime | undefined): boolean {
    if (!effectiveDate) {
      return false;
    }

    let effective: Date;

    if (typeof effectiveDate === 'string') {
      const dateMatch = effectiveDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!dateMatch) {
        return false;
      }

      const [, year, month, day] = dateMatch;
      effective = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      if (!effectiveDate.year || !effectiveDate.month || !effectiveDate.day) {
        return false;
      }
      effective = new Date(effectiveDate.year, effectiveDate.month - 1, effectiveDate.day);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    effective.setHours(0, 0, 0, 0);

    return effective.getTime() > today.getTime();
  }

  getTimeRemainingOnWaiver(terminationDate: string | SymetraDateTime | undefined): string {
    if (!terminationDate) {
      return '';
    }

    let termination: Date;

    if (typeof terminationDate === 'string') {
      const dateMatch = terminationDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!dateMatch) {
        return '';
      }

      const [, year, month, day] = dateMatch;
      termination = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      if (!terminationDate.year || !terminationDate.month || !terminationDate.day) {
        return '';
      }
      termination = new Date(terminationDate.year, terminationDate.month - 1, terminationDate.day);
    }

    const today = new Date();

    today.setHours(0, 0, 0, 0);
    termination.setHours(0, 0, 0, 0);

    const diffTime = termination.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return WaiverStatusTextEnum.WaiverExpired;
    }
    if (diffDays === 0) {
      return WaiverStatusTextEnum.ExpiresToday;
    }
    if (diffDays === 1) {
      return WaiverStatusTextEnum.OneDay;
    }

    if (diffDays > 365) {
      const years = Math.floor(diffDays / 365);
      const remainingDays = diffDays % 365;
      const weeks = Math.floor(remainingDays / 7);
      const days = remainingDays % 7;

      const parts: string[] = [];
      if (years > 0) parts.push(`${years} year${years === 1 ? '' : 's'}`);
      if (weeks > 0) parts.push(`${weeks} week${weeks === 1 ? '' : 's'}`);
      if (days > 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);

      return parts.join(', ');
    }

    if (diffDays > 7) {
      const weeks = Math.floor(diffDays / 7);
      const days = diffDays % 7;

      const parts: string[] = [];
      if (weeks > 0) parts.push(`${weeks} week${weeks === 1 ? '' : 's'}`);
      if (days > 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);

      return parts.join(', ');
    }

    return `${diffDays} days`;
  }

  /**
   * Returns the waiver display information (label and value) for a benefit
   * @param benefit - The benefit object containing effective and termination dates
   * @returns An object with label and value for the waiver display, or null if no waiver info should be shown
   */
  getWaiverDisplayInfo(benefit: IClaimBenefit): { label: string; value: string; tooltip: { heading: string; body: string } } | null {
    if (!benefit.effectiveDate) {
      return null;
    }

    if (this.isEffectiveDateInFuture(benefit.effectiveDate)) {
      return {
        label: BenefitLabelEnum.CountdownToWaiver,
        value: this.getCountdownToWaiver(benefit.effectiveDate),
        tooltip: {
          heading: BenefitLabelEnum.CountdownToWaiver,
          body: 'The period of time before the waiver of premium benefit takes effect. It is calculated by subtracting the effective date of the waiver from the current date.'
        }
      };
    } else if (benefit.terminationDate) {
      return {
        label: BenefitLabelEnum.TimeRemainingOnWaiver,
        value: this.getTimeRemainingOnWaiver(benefit.terminationDate),
        tooltip: {
          heading: BenefitLabelEnum.TimeRemainingOnWaiver,
          body: 'The remaining duration during which the waiver of premium benefit is active. ' +
                'It is calculated by subtracting the current date from the termination date of the waiver.'
        }
      };
    }

    return null;
  }

  getStatusIndicator(status: ClaimStatusEnum): StatusColorEnum {
    switch (status) {
      case ClaimStatusEnum.Open:
        return StatusColorEnum.Green;
      case ClaimStatusEnum.Pending:
        return StatusColorEnum.Yellow;
      case ClaimStatusEnum.Closed:
        return StatusColorEnum.Grey;
      default:
        return StatusColorEnum.Grey;
    }
  }

  goBackToList(): void {
    this.router.navigate(['/benefits/claims/search'], { queryParamsHandling: 'preserve' });
  }

  readonly employeeFullName = computed(() => {
    const claim = this.claimDataSignal();
    if (!claim?.employee) {
      return CommonDisplayTextEnum.Loading;
    }

    const firstName = claim.employee.firstName || '';
    const lastName = claim.employee.lastName || '';

    if (!firstName && !lastName) {
      return CommonDisplayTextEnum.Loading;
    }

    return `${firstName} ${lastName}`.trim();
  });

  readonly claimTypeDisplay = computed(() => {
    const claim = this.claimDataSignal();
    if (!claim) return CommonDisplayTextEnum.Loading;
    
    // Use enhanced logic that properly handles Waiver of Premium
    const claimTypeUi = getClaimTypeDisplayUi(
      claim.claimInfo, 
      claim.wop || false, 
      claim.claimType?.planCodesSet || []
    );
    
    return claimTypeUi.claimTypeLabel;
  });

  readonly employeeBirthdate = computed(() => {
    const claim = this.claimDataSignal();
    return this.formatBirthdateForDisplay(claim?.employee?.birthdate) || CommonDisplayTextEnum.NotAvailable;
  });

  readonly employeeSsn = computed(() => {
    const claim = this.claimDataSignal();
    return claim?.employee?.ssn || CommonDisplayTextEnum.NotAvailable;
  });

  readonly employeeGender = computed(() => {
    const claim = this.claimDataSignal();
    return claim?.employee?.gender || CommonDisplayTextEnum.NotAvailable;
  });

  readonly employeeWorkState = computed(() => {
    const claim = this.claimDataSignal();
    const stateAbbreviation = claim?.employee?.workState;
    if (!stateAbbreviation) return CommonDisplayTextEnum.NotAvailable;
    
    return StateUtilities.getFullName(stateAbbreviation);
  });

  readonly employeeWorkStateDisplay = computed(() => {
    const claim = this.claimDataSignal();
    const stateAbbreviation = claim?.employee?.workState;
    return stateAbbreviation ? StateUtilities.getFullName(stateAbbreviation) : null;
  });

  readonly employeeCaseManager = computed(() => {
    const claim = this.claimDataSignal();
    return claim?.employee?.caseManager || CommonDisplayTextEnum.NotAvailable;
  });

  readonly divisionName = computed(() => {
    const claim = this.claimDataSignal();
    return claim?.division?.name || CommonDisplayTextEnum.NotAvailable;
  });

  readonly netPaidTotal = computed(() => {
    const items = this.paymentTableItems();
    if (!items || items.length === 0) {
      return 0;
    }

    return items.reduce((sum, item) => sum + item.netPaid, 0);
  });

  onClickPaymentTableHeader(fieldName: PaymentFieldEnum) {
    if (this.paymentSortField === fieldName) {
      this.paymentSortDirection =
        this.paymentSortDirection === SearchSortDirection.Ascending
          ? SearchSortDirection.Descending
          : SearchSortDirection.Ascending;
    } else {
      this.paymentSortField = fieldName;
      this.paymentSortDirection = SearchSortDirection.Ascending;
    }
  }

  onKeydownPaymentTableHeader(event: KeyboardEvent, fieldName: PaymentFieldEnum) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onClickPaymentTableHeader(fieldName);
    }
  }

  private applySorting(items: IPaymentTableItem[]): IPaymentTableItem[] {
    const sortField = this.paymentSortFieldSignal();
    const sortDirection = this.paymentSortDirectionSignal();
    
    return [...items].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (sortField === PaymentFieldEnum.PrintDate) {
        const aDate = new Date(aValue as string);
        const bDate = new Date(bValue as string);
        const comparison = aDate.getTime() - bDate.getTime();
        return sortDirection === SearchSortDirection.Ascending ? comparison : -comparison;
      }

      if (
        sortField === PaymentFieldEnum.AmountPaid ||
        sortField === PaymentFieldEnum.InterestPaid ||
        sortField === PaymentFieldEnum.NetPaid
      ) {
        const comparison = (aValue as number) - (bValue as number);
        return sortDirection === SearchSortDirection.Ascending ? comparison : -comparison;
      }

      const comparison = (aValue as string).localeCompare(bValue as string);
      return sortDirection === SearchSortDirection.Ascending ? comparison : -comparison;
    });
  }

  private loadClaimDetail(claimId: string): void {
    this.isLoadingSignal.set(true);

    console.log('Loading claim with ID from route:', claimId);

    const claim = this.localClaimsDataReaderService.getClaimById(claimId);

    if (claim) {
      this.claimData = claim;
      console.log('Loaded claim data:', claim);
      console.log('Claim ID from data:', claim.claimDetails.claimId);

      const titleText = `${ClaimDetailComponent.PAGE_TITLE} (${claim.claimDetails.claimId})`;
      this.title.setTitle(titleText);

      this.claimDataSignal.set(claim);
    } else {
      console.log('Claim not found with ID:', claimId);
      this.claimData = null;

      this.claimDataSignal.set(null);

      const fallbackTitle = `${ClaimDetailComponent.PAGE_TITLE} (${claimId})`;
      this.title.setTitle(fallbackTitle);
    }

    this.isLoadingSignal.set(false);
  }

  getProgressTrackerItems(): IProgressTrackerItem[] {
    if (!this.claimData?.progressTracker?.steps) {
      return [];
    }

    return this.claimData.progressTracker.steps.map((step: IProgressStep) => {
      let variant: IProgressTrackerItem['variant'] = 'future-item'; // default

      switch (step.status) {
        case 'complete':
          variant = 'complete';
          break;
        case 'active':
          variant = 'active';
          break;
        case 'active-error':
          variant = 'blocked';
          break;
        case 'future':
        default:
          variant = 'future-item';
          break;
      }

      let labelText: string;
      
      switch (step.type) {
        case 'received':
        case 'submitted':
          labelText = ProgressTrackerLabelEnum.Received;
          break;
        case 'processing':
          labelText = ProgressTrackerLabelEnum.Processing;
          break;
        case 'processed':
          labelText = ProgressTrackerLabelEnum.Processed;
          break;
        case 'pending':
          labelText = ProgressTrackerLabelEnum.Pending;
          break;
        case 'closed':
          labelText = ProgressTrackerLabelEnum.Closed;
          break;
        default:
          labelText = step.title || CommonDisplayTextEnum.Unknown;
          break;
      }

      return {
        variant,
        labelText,
        messageText: step.date ? this.formatDate(step.date) : step.description || ''
      };
    });
  }

  getClaimStartLabel(): string {
    const claimType = this.claimData?.claimInfo?.claimType;
    
    let enumLabel: string;
    switch (claimType) {
      case ClientClaimTypeEnum.Life:
        enumLabel = ProgressSectionLabelEnum.DateOfDeathClaimStart;
        break;
      case ClientClaimTypeEnum.AccidentalDeathAndDismemberment:
        enumLabel = ProgressSectionLabelEnum.DateOfAccidentClaimStart;
        break;
      case ClientClaimTypeEnum.WaiverOfPremium:
        enumLabel = ProgressSectionLabelEnum.DateOfDisabilityClaimStart;
        break;
      default:
        enumLabel = ProgressSectionLabelEnum.ClaimStartDate;
        break;
    }
    
    const customLabel = this.claimData?.claimInfo?.claimStartLabel;
    return customLabel || enumLabel;
  }

  getDaysAgo(date: SymetraDateTime | string | undefined | null): string {
    if (!date) {
      return '';
    }

    let targetDate: Date;

    if (typeof date === 'string') {
      targetDate = new Date(date);
    } else {
      if (date.year !== undefined && date.month !== undefined && date.day !== undefined) {
        targetDate = new Date(date.year, date.month - 1, date.day);
      } else {
        return '';
      }
    }

    const today = new Date();
    const diffTime = Math.abs(today.getTime() - targetDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return `${diffDays} days ago`;
  }

  formatKeyDate(date: SymetraDateTime | string | undefined | null): string {
    if (!date) {
      return CommonDisplayTextEnum.NotAvailable;
    }

    let targetDate: Date;

    if (typeof date === 'string') {
      // Parse date string as local date to avoid timezone issues
      const dateMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        targetDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        targetDate = new Date(date);
      }
    } else {
      if (date.year !== undefined && date.month !== undefined && date.day !== undefined) {
        targetDate = new Date(date.year, date.month - 1, date.day);
      } else {
        return CommonDisplayTextEnum.NotAvailable;
      }
    }

    return targetDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getKeyDatesClaimStartLabel(): string {
    const claimType = this.claimData?.claimInfo?.claimType;
    
    let enumLabel: string;
    switch (claimType) {
      case ClientClaimTypeEnum.Life:
        enumLabel = KeyDateLabelEnum.DateOfDeathClaimStart;
        break;
      case ClientClaimTypeEnum.AccidentalDeathAndDismemberment:
        enumLabel = KeyDateLabelEnum.DateOfAccidentClaimStart;
        break;
      case ClientClaimTypeEnum.WaiverOfPremium:
        enumLabel = KeyDateLabelEnum.DateOfDisability;
        break;
      default:
        enumLabel = KeyDateLabelEnum.ClaimStartDate;
        break;
    }
    
    const customLabel = this.claimData?.keyDates?.claimStartLabel || this.claimData?.claimInfo?.claimStartLabel;
    return customLabel || enumLabel;
  }

  getBenefitStatusColor(statusColor?: string): StatusColorEnum {
    if (statusColor === 'default' || !statusColor) {
      return StatusColorEnum.Grey;
    }
    return Object.values(StatusColorEnum).includes(statusColor as StatusColorEnum)
      ? (statusColor as StatusColorEnum)
      : StatusColorEnum.Grey;
  }

  toNumber(value: string | number | null | undefined): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  formatCurrency(amount: number | undefined): string {
    if (amount === undefined || amount === null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  trackByBenefitId(index: number, benefit: IClaimBenefit): string | number {
    return benefit?.title || index;
  }

  trackByStatusId(index: number, status: IBenefitStatus): string | number {
    return status?.status || index;
  }

  readonly caseManagerDisplay = computed(() => {
    const claim = this.claimDataSignal();
    return claim?.claimInfo?.caseManager || claim?.employee?.caseManager || CommonDisplayTextEnum.NotAvailable;
  });

  readonly fallbackClaimTypeLabel = computed(() => {
    const claim = this.claimDataSignal();
    return claim?.claimType?.claimTypeLabel || 'Claim benefit';
  });

  readonly claimStatus = computed(() => {
    const claim = this.claimDataSignal();
    return claim?.status || 'Pending';
  });

  readonly claimStartDate = computed(() => {
    const claim = this.claimDataSignal();
    return this.formatDate(claim?.claimInfo?.claimStartDate || claim?.started?.claimDate);
  });

  readonly keyDateClaimStart = computed(() => {
    const claim = this.claimDataSignal();
    return this.formatKeyDate(claim?.keyDates?.claimStart || claim?.started?.claimDate);
  });

  readonly keyDateClaimSubmitted = computed(() => {
    const claim = this.claimDataSignal();
    return this.formatKeyDate(claim?.keyDates?.claimSubmitted || claim?.received);
  });

  readonly claimBenefits = computed(() => {
    const claim = this.claimDataSignal();
    return claim?.benefits || [];
  });

  readonly benefitListCardInputs = computed(() => {
    return this.claimBenefits().map(benefit => ({
      title: benefit.title,
      statuses: this.mapBenefitStatusesToListCard(benefit.statuses),
      benefit: benefit
    }));
  });

  readonly fallbackBenefitListCardInput = computed(() => ({
    title: this.fallbackClaimTypeLabel(),
    statuses: [{
      text: this.claimStatus(),
      color: 'grey' as const,
      variant: 'subtle' as const
    }] as StatusIndicatorInput[],
    benefit: null
  }));

  getBenefitDivisionClass(benefit: IClaimBenefit): string {
    return benefit?.divisionClass || CommonDisplayTextEnum.NotAvailable;
  }

  getBenefitReason(benefit: IClaimBenefit): string {
    return benefit?.reason || CommonDisplayTextEnum.NotAvailable;
  }

  mapBenefitStatusesToListCard(statuses: IBenefitStatus[]): StatusIndicatorInput[] {
    return statuses.map(status => ({
      text: status.status,
      color: this.mapStatusColorToListCard(status.statusColor),
      variant: 'subtle' as const
    }));
  }

  private mapStatusColorToListCard(statusColor?: string): StatusIndicatorInput['color'] {
    switch (statusColor) {
      case 'red':
        return 'red';
      case 'green':
        return 'green';
      case 'default':
      case 'grey':
      default:
        return 'grey';
    }
  }
}
